import { DragRef, Point } from '@angular/cdk/drag-drop';
import { Component, OnInit, OnDestroy, ElementRef, Renderer2 } from '@angular/core';

/**
 * Yes:はい、No:いいえ、Ok:ＯＫ、Cancel:キャンセル、Yes_Error：エラーダイアログ用の「はい」
 */
export const enum PlessAlert {
  /** I0508やQ0100などInfoやQuestion系で使用 */
  Yes = 1,
  /** Q0100などQuestion系で使用 */
  No = 2,
  /** W0030やE0118、S0100など警告やエラー系で使用 */
  Yes_Error = 9,
}

/* 確認ダイアログの「はい」ボタンのラベル */
export const LABEL_YES = 'はい';
/* 「いいえ」ボタンのラベル */
export const LABEL_NO = 'いいえ';
/* エラーダイアログの「はい」ボタンのラベル*/
export const LABEL_YESERROR = 'はい';

@Component({
  selector: 'pless-alert',
  templateUrl: './pless-alert.component.html',
  styleUrls: ['./pless-alert.component.scss'],
})

export class PlessAlertComponent {

  /** タイトル */
  title = '';
  /** 表示文字列を行ごとに格納 */
  textList = [];
  /** 表示ボタン */
  buttonList = [];

  /** アラートを閉じる */
  closeFunction: Function;
  /** アラート閉じる時に実行する処理 */
  closeHander: Function;
  /** ボタンの選択値を保持 */
  selectedIndex: number;
  /** ダイアログ表示完了フラグ */
  viewFlag = false;

  constructor(public elementRef: ElementRef, public renderer: Renderer2) { }

  /** ボタンが押された場合の処理 */
  onClick(value: number) {
    if (this.closeFunction != null) {
      this.closeFunction();       // アラートを閉じる
    }
    if (this.closeHander != null) {
      this.closeHander(value);    // アラートを閉じる際の処理を実行(ボタンの値を渡す)
    }
  }

  /**
   * [Tab],[Shift+Tab]押下イベントを取得し、
   * ボタンのフォーカスを制御する
   *
   * @param {Event} event キーダウンイベント
   * @param {number} value 現在のボタン選択値
   * @param {boolean} shiftFlg シフトキー押下フラグ
   * @memberof PlessAlertComponent
   */
  onKeyTab(event: Event, value: number, shiftFlg: boolean): void {
    let selectedButton;

    for (let i = Number(0); i < this.buttonList.length; i++) {
      if (this.buttonList[i]['id'] === value) {
        selectedButton = i;
      }
    }

    // Shift+Tabか判定
    if (shiftFlg) {
      // 現在の選択値がボタンのindex0を下回るか
      if (selectedButton - 1 < 0) {
        this.selectedIndex = this.buttonList.length - 1;
        this.setButtonFocus(this.buttonList[Number(this.selectedIndex)]['id']);
      } else {
        this.selectedIndex = selectedButton - 1;
        this.setButtonFocus(this.buttonList[Number(this.selectedIndex)]['id']);
      }
    } else {
      // 現在の選択値がボタンのlength値か
      if (this.buttonList.length === selectedButton + 1) {
        this.selectedIndex = 0;
        this.setButtonFocus(this.buttonList[Number(this.selectedIndex)]['id']);
      } else {
        this.selectedIndex = selectedButton + 1;
        this.setButtonFocus(this.buttonList[Number(this.selectedIndex)]['id']);
      }
    }
    event.preventDefault();   // イベントを無効化
    event.stopPropagation();  // 上位へのイベントの伝播をキャンセル
  }

  /**
   * ボタン外のをクリックした際のフォーカス制御
   *
   * @param {Event} event クリックイベント
   * @memberof PlessAlertComponent
   */
  public onClickedOutside(event: Event): void {
    if (this.viewFlag) {
      // 現在のボタン選択値にフォーカスをあてる
      this.setButtonFocus(this.buttonList[Number(this.selectedIndex)]['id']);

      event.preventDefault();   // イベントを無効化
      event.stopPropagation();  // 上位へのイベントの伝播をキャンセル
    }
  }

  /**
   * アラートに表示するデータを設定
   *
   * @param {string} text 表示する文字列(改行は\nで指定)
   * @param {string} [title] アラートのタイトル
   * @param {Array<PlessAlert>} [buttons] 表示するボタン
   * @param {Number} [defaultButton] デフォルト選択ボタン
   * @memberof PlessAlertComponent
   */
  setData(text: string, title?: string, buttons?: Array<PlessAlert>, defaultButton?: number) {
    // 表示上改行できるように表示文字列を改行ごとに分割
    this.textList = text.split('\n');
    // タイトルを設定
    this.title = title;
    // 指定されたボタンを指定順に設定
    this.buttonList = [];
    if (buttons != null) {
      for (let i = 0; i < buttons.length; i++) {
        if (buttons[i] === PlessAlert.Yes) {
          this.buttonList.push({ id: PlessAlert.Yes, label: LABEL_YES });
        } else if (buttons[i] === PlessAlert.No) {
          this.buttonList.push({ id: PlessAlert.No, label: LABEL_NO });
        } else if (buttons[i] === PlessAlert.Yes_Error) {
          this.buttonList.push({ id: PlessAlert.Yes_Error, label: LABEL_YESERROR });
        }
      }
    } else {
      // ボタン指定が無い場合は「はい」のみ表示
      this.buttonList.push({ id: PlessAlert.Yes, label: LABEL_YES });
    }

    // 中央に表示する
    this.moveCenter();

    // デフォルトボタンが連携されているか
    if (defaultButton === undefined) {
      // 左端のボタンにフォーカスを当てる
      this.selectedIndex = 0;
      this.setButtonFocus(this.buttonList[0]['id']);
    } else {
      // XMLから取得したデフォルトボタンにフォーカルを当てる
      for (let i = Number(0); i < this.buttonList.length; i++) {
        if (this.buttonList[i]['id'] === defaultButton) {
          this.selectedIndex = i;
        }
      }
      this.setButtonFocus(Number(defaultButton));
    }
  }

  /**
   * アラートを中央(bodyの真ん中)に移動する
   */
  moveCenter() {
    setTimeout(() => {
      const bodyElement = document.body;
      const alertElement = this.elementRef.nativeElement.childNodes[0];
      const bodyRect = bodyElement.getBoundingClientRect();
      const alertRect = alertElement.getBoundingClientRect();
      const top = (bodyRect.height / 2) - (alertRect.height / 2);
      const left = (bodyRect.width / 2) - (alertRect.width / 2);
      this.renderer.setStyle(alertElement, 'top', top + 'px');
      this.renderer.setStyle(alertElement, 'left', left + 'px');
      this.renderer.setStyle(alertElement, 'visibility', 'visible'); // 移動する描写が見えるので、最初非表示で移動後にvisibleにする
    }, 1);
  }

  /**
   * 指定のボタンにフォーカスを当てる
   * @param buttonId ボタンのID(PlessAlert.Yesなど)
   */
  setButtonFocus(buttonId: number) {
    setTimeout(() => {      // すぐに実行するとフォーカスが当たらないので1ms後に実行
      const buttonElement = document.getElementById('pless-alert-button-' + buttonId);
      if (buttonElement != null) {
        buttonElement.focus();
        this.viewFlag = true;
      }
    }, 1);
  }

  /**
   * cdkDragConstrainPositionで呼ばれるダイアログをViewPortから隠れないようにする座標調整関数
   */
  constrainPosition = (
    _pointerPos: Point,
    dragRef: DragRef<any>,
    elementRect: ClientRect,
    _pickupPos: Point
  ): Point => {
    // ── 1) 要素サイズは elementRect から取得
    const width = elementRect.width;
    const height = elementRect.height;
    // ── 2) カーソルのウィンドウ座標から要素内で掴んだ相対座標を引いて、「要素左上が置かれるべき座標」を求める
    const rawX = _pointerPos.x - _pickupPos.x;
    const rawY = _pointerPos.y - _pickupPos.y;
    // ── 3) 残したい見える領域（handle 部分）のサイズ
    const handleWidth = 60;  // 左端に残す幅
    const handleHeight = 60;  // 上端に残す高さ
    // ── 4) ビューポートサイズ
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // ── 5) clamp 用の最小／最大値を算出
    const minX = -width + handleWidth;
    const maxX = vw - handleWidth;
    const minY = -height + handleHeight;
    const maxY = vh - handleHeight;
    // ── 6) 移動座標を範囲内にクランプして返す
    return {
      x: Math.max(minX, Math.min(maxX, rawX)),
      y: Math.max(minY, Math.min(maxY, rawY))
    };
  }
}
